import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ShieldCheck, RefreshCw, Play, AlertTriangle, Activity } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

/**
 * Auto Malfunction Guard — live payout/wager ratios per game with a safe-limit
 * threshold. Games over the limit are auto-paused; operator can tune the limit
 * and resume a paused game here.
 */
export const MalfunctionGuardPanel = () => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [form, setForm] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await axios.get(`${API}/admin/guard/status`, { withCredentials: true });
            setData(data);
            setForm(data.config);
        } catch {
            toast.error('Could not load the Malfunction Guard');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
        const t = setInterval(load, 30000);
        return () => clearInterval(t);
    }, [load]);

    const saveConfig = async (patch) => {
        setSaving(true);
        try {
            const { data } = await axios.patch(`${API}/admin/guard/config`, patch, { withCredentials: true });
            setForm(data);
            toast.success('Guard settings saved');
            load();
        } catch {
            toast.error('Could not save settings');
        } finally {
            setSaving(false);
        }
    };

    const scanNow = async () => {
        try {
            const { data } = await axios.post(`${API}/admin/guard/scan-now`, {}, { withCredentials: true });
            toast.info(data.tripped?.length ? `${data.tripped.length} game(s) tripped` : 'Scan clean — nothing over the limit');
            load();
        } catch {
            toast.error('Scan failed');
        }
    };

    const resume = async (game) => {
        if (!window.confirm(`Resume ${game}? It will be playable again.`)) return;
        try {
            await axios.post(`${API}/admin/guard/resume`, { game }, { withCredentials: true });
            toast.success(`${game} resumed`);
            load();
        } catch {
            toast.error('Resume failed');
        }
    };

    const cfg = form || {};
    const games = data?.games || [];
    const paused = data?.paused || [];

    return (
        <div className="rounded-2xl border border-[#39FF14]/25 bg-[#0a140c] p-4 sm:p-5 mt-6" data-testid="guard-panel">
            <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-xl bg-[#39FF14]/15 flex items-center justify-center shrink-0">
                        <ShieldCheck className="w-5 h-5 text-[#39FF14]" />
                    </div>
                    <div>
                        <h3 className="text-base font-black text-[#39FF14] tracking-wide">AUTO MALFUNCTION GUARD</h3>
                        <p className="text-[11px] text-white/50 max-w-xl">
                            Watches each game's live payout-to-wager ratio. Any game paying back more than your limit is auto-paused and you're alerted. Break the Piggy Bank is excluded (already off).
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={scanNow} className="flex items-center gap-1.5 rounded-lg bg-white/10 hover:bg-white/20 text-white text-xs font-bold px-3 py-2" data-testid="guard-scan-now">
                        <Activity className="w-4 h-4" /> Scan now
                    </button>
                    <button onClick={load} disabled={loading} className="p-2 rounded-lg hover:bg-white/10 text-white/60 disabled:opacity-40" data-testid="guard-refresh">
                        <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                </div>
            </div>

            {/* config */}
            <div className="flex flex-wrap items-end gap-3 mb-4 rounded-xl border border-white/10 bg-white/[0.02] p-3">
                <Field label="Safe limit (×)" hint="pause above this return">
                    <input type="number" step="0.5" min="1.5" value={cfg.threshold ?? ''} onChange={(e) => setForm({ ...cfg, threshold: parseFloat(e.target.value) })}
                        className="w-20 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-white text-sm" data-testid="guard-threshold" />
                </Field>
                <Field label="Window (min)">
                    <input type="number" min="5" value={cfg.window_min ?? ''} onChange={(e) => setForm({ ...cfg, window_min: parseInt(e.target.value, 10) })}
                        className="w-20 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-white text-sm" data-testid="guard-window" />
                </Field>
                <Field label="Min wagered ($)" hint="ignore low volume">
                    <input type="number" min="0" value={cfg.min_wager ?? ''} onChange={(e) => setForm({ ...cfg, min_wager: parseFloat(e.target.value) })}
                        className="w-24 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-white text-sm" data-testid="guard-minwager" />
                </Field>
                <Field label="Min plays">
                    <input type="number" min="1" value={cfg.min_events ?? ''} onChange={(e) => setForm({ ...cfg, min_events: parseInt(e.target.value, 10) })}
                        className="w-20 bg-black/40 border border-white/15 rounded-lg px-2 py-1 text-white text-sm" data-testid="guard-minevents" />
                </Field>
                <label className="flex items-center gap-2 text-xs text-white/70 cursor-pointer">
                    <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setForm({ ...cfg, enabled: e.target.checked })} data-testid="guard-enabled" />
                    Guard on
                </label>
                <button onClick={() => saveConfig(cfg)} disabled={saving} className="rounded-lg bg-[#39FF14] text-black font-black text-xs px-3 py-2 hover:brightness-110 disabled:opacity-40" data-testid="guard-save">Save</button>
            </div>

            {/* paused games */}
            {paused.length > 0 && (
                <div className="mb-4 rounded-xl border border-[#ff5470]/40 bg-[#ff5470]/10 p-3" data-testid="guard-paused">
                    <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-wider text-[#ff5470] mb-2">
                        <AlertTriangle className="w-4 h-4" /> Auto-paused
                    </div>
                    {paused.map((p) => (
                        <div key={p.game} className="flex items-center justify-between py-1 text-xs">
                            <span className="text-white">{p.game} <span className="text-white/40">— {p.ratio}× ({money(p.payout)} on {money(p.wager)})</span></span>
                            <button onClick={() => resume(p.game)} className="flex items-center gap-1 rounded-lg bg-[#39FF14]/15 text-[#39FF14] border border-[#39FF14]/40 font-bold text-[11px] px-2 py-1" data-testid={`guard-resume-${p.game}`}>
                                <Play className="w-3 h-3" /> Resume
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* live ratios */}
            <div className="overflow-x-auto">
                <table className="w-full text-xs" data-testid="guard-table">
                    <thead>
                        <tr className="text-white/40 text-[10px] uppercase tracking-wider">
                            <th className="text-left py-1.5">Game (last {data?.window_min ?? 30}m)</th>
                            <th className="text-right py-1.5">Wagered</th>
                            <th className="text-right py-1.5">Paid</th>
                            <th className="text-right py-1.5">Return</th>
                            <th className="text-right py-1.5">Plays</th>
                        </tr>
                    </thead>
                    <tbody>
                        {games.length === 0 && (
                            <tr><td colSpan={5} className="text-white/40 text-center py-4">No game activity in the window.</td></tr>
                        )}
                        {games.map((g) => (
                            <tr key={g.game} className="border-t border-white/5" data-testid={`guard-row-${g.game}`}>
                                <td className="py-1.5 text-white">{g.label}{!g.pausable && <span className="text-white/30"> (alert-only)</span>}</td>
                                <td className="py-1.5 text-right font-mono text-white/60">{money(g.wager)}</td>
                                <td className="py-1.5 text-right font-mono text-white/60">{money(g.payout)}</td>
                                <td className={`py-1.5 text-right font-mono font-bold ${g.over_limit ? 'text-[#ff5470]' : g.judged ? 'text-[#39FF14]' : 'text-white/30'}`}>
                                    {g.ratio == null ? '—' : `${g.ratio}×`}{g.over_limit ? ' ⚠' : ''}
                                </td>
                                <td className="py-1.5 text-right font-mono text-white/50">{g.events}{!g.judged && <span className="text-white/25"> (low)</span>}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

const Field = ({ label, hint, children }) => (
    <div>
        <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">{label}{hint && <span className="text-white/25 normal-case tracking-normal"> · {hint}</span>}</div>
        {children}
    </div>
);

export default MalfunctionGuardPanel;
